package team.platforma.multiplatform_material_extension.bottom_sheet

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.foundation.gestures.detectVerticalDragGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBars
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.layout.wrapContentSize
import androidx.compose.material3.BottomSheetDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.ModalBottomSheetProperties
import androidx.compose.material3.SheetValue
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.IntOffset
import kotlinx.coroutines.launch
import team.platforma.multiplatform_material_extension.bottom_sheet.style.CommonBottomSheetStyle
import team.platforma.multiplatform_material_extension.bottom_sheet.utils.CommonBottomSheetState
import team.platforma.multiplatform_material_extension.bottom_sheet.utils.SheetHelper
import team.platforma.multiplatform_material_extension.bottom_sheet.utils.rememberSheetHelper
import kotlin.math.roundToInt

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CommonBottomSheet(
    modifier: Modifier = Modifier,
    onDismiss: () -> Unit,
    changeVisibleState: Boolean = true,
    fullScreen: Boolean = true,
    dragHandle: @Composable (BoxScope.(sheetHelper: SheetHelper, color: Color) -> Unit) = { sheetHelper, color ->
        CommonBottomSheetStyle.dragHandle(color)
    },
    dismissOnBackPressed: Boolean = true,
    userDismiss: Boolean = true,
    contentWindowInsets: @Composable () -> WindowInsets = { BottomSheetDefaults.windowInsets },
    content: @Composable ((sheetHelper: SheetHelper) -> Unit),
) {

    val coroutineScope = rememberCoroutineScope()

    val sheetHelperInternal = rememberSheetHelper()

    val sheetState = rememberModalBottomSheetState(
        skipPartiallyExpanded = fullScreen,
        confirmValueChange = { it != SheetValue.Hidden }
    )
    val commonBottomSheetState = CommonBottomSheetState

    if (changeVisibleState) {
        DisposableEffect(Unit) {
            commonBottomSheetState.setState(true)
            onDispose {
                commonBottomSheetState.setState(false)
            }
        }
    }

    val listener = object : SheetHelper {
        override fun close() {
            coroutineScope.launch {
                onDismiss()
                sheetState.hide()
            }
        }
    }

    DisposableEffect(sheetHelperInternal) {

        sheetHelperInternal.addListener(listener)

        onDispose {
            sheetHelperInternal.removeListener(listener)
        }
    }

    val dragOffsetY = remember { Animatable(0f) }

    var contentHeight by remember { mutableStateOf(0f) }

    val style = CommonBottomSheetStyle

    ModalBottomSheet(
        modifier = Modifier.padding(
            top = WindowInsets.statusBars.asPaddingValues().calculateTopPadding()
        ).offset { IntOffset(0, dragOffsetY.value.roundToInt()) }
            .then(
                modifier
            ),
        onDismissRequest = {
            sheetHelperInternal.close()
        },
        shape = style.shape(),
        sheetState = sheetState,
        properties = ModalBottomSheetProperties(
            shouldDismissOnBackPress = dismissOnBackPressed
        ),
        tonalElevation = style.tonalElevation(),
        containerColor = style.background(),
        contentWindowInsets = contentWindowInsets,
        dragHandle = {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .wrapContentHeight()
                    .pointerInput(Unit) {
                        detectVerticalDragGestures(
                            onDragEnd = {
                                coroutineScope.launch {
                                    if (dragOffsetY.value > contentHeight.toDouble() / 2) {
                                        if (userDismiss) {
                                            sheetHelperInternal.close()
                                        } else {
                                            dragOffsetY.animateTo(0f, tween(250))
                                        }
                                    } else {
                                        dragOffsetY.animateTo(0f, tween(250))
                                    }
                                }
                            },
                            onVerticalDrag = { _, dragAmount ->
                                coroutineScope.launch {
                                    dragOffsetY.snapTo(
                                        (dragOffsetY.value + dragAmount).coerceAtLeast(
                                            0f
                                        )
                                    )
                                }
                            }
                        )
                    },
                contentAlignment = Alignment.Center
            ) {
                dragHandle(listener, style.dragHandleColor())
            }
        }
    ) {
        BoxWithConstraints(
            modifier = Modifier.wrapContentSize()
        ) {
            contentHeight = maxHeight.value
            content(listener)
        }
    }

}